import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, Mail, Search, ShieldCheck, UserPlus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminAccessService, type AdminAccessRecord } from '../lib/supabase';

/**
 * Campaign → Admin access. Superadmin-only management of who holds admin access.
 *
 * Every mutation goes through the `manage-admin-access` edge function, which re-checks the caller's
 * is_superadmin flag server-side. Nothing here writes to the `admin` table directly, and no control
 * hidden by this component is the only thing standing between a user and the action.
 */

/** Matches the server-side check in `manage-admin-access`, so the two cannot disagree. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Rows per page, so the table stays a fixed height as the roster grows. */
const ADMINS_PER_PAGE = 10;

export interface AdminAccessPanelProps {
  /** Signed-in superadmin's email — used to suppress the Remove control on their own row. */
  currentUserEmail: string;
}

function formatAddedDate(record: AdminAccessRecord): string {
  const raw = record.invited_at ?? record.created_at;
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const StatusPill: React.FC<{ status: AdminAccessRecord['status'] }> = ({ status }) => {
  const isActive = status === 'active';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
      }`}
    >
      {isActive ? 'Active' : 'Pending sign-in'}
    </span>
  );
};

/** Shell shared by the add-admin form and the remove confirmation, matching `MessageModal`. */
const ModalShell: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-white/50"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-charcoal">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-gray hover:text-charcoal transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </motion.div>
  </motion.div>
);

const AdminAccessPanel: React.FC<AdminAccessPanelProps> = ({ currentUserEmail }) => {
  const [admins, setAdmins] = useState<AdminAccessRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<AdminAccessRecord | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  /** Email currently being resent, so only that row shows a spinner. */
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);

  /** 1-based page for the roster table. */
  const [page, setPage] = useState(1);

  const normalizedCurrentUser = currentUserEmail.trim().toLowerCase();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setAdmins(await adminAccessService.listAdmins());
    } catch (err) {
      console.error('AdminAccessPanel load:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load admins.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter((a) => a.email.toLowerCase().includes(q));
  }, [admins, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ADMINS_PER_PAGE));

  /**
   * Keeps the current page inside the available range. Needed because the list can shrink under
   * the viewer — searching narrows it, and removing the last admin on the final page would
   * otherwise leave them looking at an empty table.
   */
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const visible = useMemo(
    () => filtered.slice((page - 1) * ADMINS_PER_PAGE, page * ADMINS_PER_PAGE),
    [filtered, page]
  );

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    setAddEmail('');
    setAddError(null);
  }, []);

  const handleAdd = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const email = addEmail.trim().toLowerCase();

      if (!email) {
        setAddError('Enter an email address.');
        return;
      }
      if (!EMAIL_RE.test(email)) {
        setAddError('Enter a valid email address.');
        return;
      }
      if (admins.some((a) => a.email.toLowerCase() === email)) {
        setAddError('That email already has admin access.');
        return;
      }

      setAdding(true);
      setAddError(null);
      try {
        const result = await adminAccessService.inviteAdmin(email);
        closeAdd();
        // The grant succeeded even when the email did not. That is reported as an error rather than
        // a caveat on a success toast, because it is the one outcome the superadmin has to act on.
        if (result.emailSent) {
          toast.success(`Invite sent to ${email}.`);
        } else {
          toast.error(`${email} now has admin access, but the invite email could not be sent. Use Resend invite.`);
        }
        await load();
      } catch (err) {
        setAddError(err instanceof Error ? err.message : 'Could not add that admin.');
      } finally {
        setAdding(false);
      }
    },
    [addEmail, admins, closeAdd, load]
  );

  const handleResend = useCallback(async (email: string) => {
    setResendingEmail(email);
    try {
      await adminAccessService.resendInvite(email);
      toast.success(`Invite resent to ${email}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not resend that invite.');
    } finally {
      setResendingEmail(null);
    }
  }, []);

  const handleRemove = useCallback(async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await adminAccessService.removeAdmin(removeTarget.email);
      setRemoveTarget(null);
      toast.success(`${removeTarget.email} no longer has admin access.`);
      await load();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Could not remove that admin.');
    } finally {
      setRemoving(false);
    }
  }, [removeTarget, load]);

  const renderRows = () =>
    visible.map((admin) => {
      const isSelf = admin.email.toLowerCase() === normalizedCurrentUser;
      // Two separate reasons a row cannot be removed, and both are enforced server-side too.
      const canRemove = !isSelf && !admin.is_superadmin;
      return (
        <tr key={admin.id} className="border-b border-charcoal/10 last:border-b-0 align-top">
          <td className="p-3">
            <div className="flex items-center gap-2 font-medium text-charcoal break-all">
              {admin.email}
              {admin.is_superadmin && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-electric-blue/10 px-2 py-0.5 text-[11px] font-semibold text-electric-blue"
                  title="Superadmin — can manage admin access"
                >
                  <ShieldCheck className="w-3 h-3" aria-hidden />
                  Superadmin
                </span>
              )}
            </div>
            {admin.invited_by && (
              <div className="mt-0.5 text-xs text-slate-gray break-all">
                Added by {admin.invited_by}
              </div>
            )}
          </td>
          <td className="p-3">
            <StatusPill status={admin.status} />
          </td>
          <td className="p-3 text-slate-gray whitespace-nowrap">{formatAddedDate(admin)}</td>
          <td className="p-3 text-right whitespace-nowrap">
            <div className="inline-flex items-center gap-3">
              {admin.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => void handleResend(admin.email)}
                  disabled={resendingEmail === admin.email}
                  className="text-sm font-medium text-electric-blue hover:underline disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {resendingEmail === admin.email ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Mail className="w-3.5 h-3.5" aria-hidden />
                  )}
                  Resend invite
                </button>
              )}
              {canRemove && (
                <button
                  type="button"
                  onClick={() => {
                    setRemoveError(null);
                    setRemoveTarget(admin);
                  }}
                  className="text-sm font-medium text-red-600 hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
          </td>
        </tr>
      );
    });

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-electric-blue" aria-hidden />
          <span className="sr-only">Loading admins</span>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="py-8 text-center">
          <p className="text-charcoal font-medium mb-4">{loadError}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl"
          >
            Retry
          </button>
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <p className="py-12 text-center text-sm text-slate-gray">
          {search.trim() ? 'No admins match that search.' : 'No admins yet.'}
        </p>
      );
    }

    return (
      <div className="overflow-auto rounded-xl border border-charcoal/10">
        <table className="w-full text-sm border-collapse">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-gray">
            <tr>
              <th scope="col" className="p-3 bg-white border-b border-charcoal/15">
                Person
              </th>
              <th scope="col" className="p-3 bg-white border-b border-charcoal/15">
                Status
              </th>
              <th scope="col" className="p-3 bg-white border-b border-charcoal/15">
                Added
              </th>
              <th scope="col" className="p-3 bg-white border-b border-charcoal/15 text-right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
    );
  };

  const renderPagination = () => {
    if (loading || loadError || totalPages <= 1) return null;
    return (
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-sm text-slate-gray">
          Page {page} of {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" aria-hidden />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <label htmlFor="admin-access-search" className="sr-only">
            Search admins by email
          </label>
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray pointer-events-none"
            aria-hidden
          />
          <input
            id="admin-access-search"
            type="search"
            placeholder="Search by email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 text-sm rounded-xl border border-charcoal/15 bg-white text-charcoal placeholder:text-slate-gray/70 shadow-sm focus:outline-none focus:ring-2 focus:ring-electric-blue/30 focus:border-electric-blue/40"
          />
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="px-4 py-2.5 text-sm font-semibold rounded-xl bg-gradient-primary text-white hover:shadow-md inline-flex items-center justify-center gap-2 shrink-0"
        >
          <UserPlus className="w-4 h-4" aria-hidden />
          Add admin
        </button>
      </div>

      {renderBody()}
      {renderPagination()}

      <AnimatePresence>
        {addOpen && (
          <ModalShell title="Add admin" onClose={closeAdd}>
            <form onSubmit={handleAdd} noValidate>
              <label
                htmlFor="admin-access-new-email"
                className="block text-sm font-medium text-charcoal mb-1.5"
              >
                Email address
              </label>
              <input
                id="admin-access-new-email"
                type="email"
                autoFocus
                value={addEmail}
                onChange={(e) => {
                  setAddEmail(e.target.value);
                  setAddError(null);
                }}
                placeholder="name@company.com"
                aria-invalid={addError ? 'true' : undefined}
                aria-describedby={addError ? 'admin-access-add-error' : undefined}
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-charcoal/15 bg-white text-charcoal placeholder:text-slate-gray/70 shadow-sm focus:outline-none focus:ring-2 focus:ring-electric-blue/30 focus:border-electric-blue/40"
              />
              <p className="mt-2 text-xs text-slate-gray">
                An account is created immediately and an invite email is sent. No sign-up step.
              </p>

              {addError && (
                <p
                  id="admin-access-add-error"
                  role="alert"
                  className="mt-3 flex items-start gap-2 text-sm text-red-600"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                  {addError}
                </p>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={closeAdd}
                  className="flex-1 py-2.5 rounded-xl border border-charcoal/20 text-charcoal font-medium hover:bg-charcoal/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-primary text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {adding && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
                  {adding ? 'Adding…' : 'Add admin'}
                </button>
              </div>
            </form>
          </ModalShell>
        )}

        {removeTarget && (
          <ModalShell title="Remove admin access" onClose={() => setRemoveTarget(null)}>
            <p className="text-sm text-slate-gray">
              <span className="font-medium text-charcoal break-all">{removeTarget.email}</span> will
              immediately lose admin access. Their user account is not deleted.
            </p>

            {removeError && (
              <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                {removeError}
              </p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                className="flex-1 py-2.5 rounded-xl border border-charcoal/20 text-charcoal font-medium hover:bg-charcoal/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={removing}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {removing && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
                {removing ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAccessPanel;
